import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import { Loader } from "@/components/common";

export default function Bordereau() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/deliveries/${id}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="p-10 text-center text-slate-500">Bordereau introuvable ou accès refusé.</div>;
  if (!data) return <Loader label="Chargement du bordereau..." />;

  const { delivery: d, order: o, tour: t, company: c } = data;
  const items = o?.items || [];

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-3 print:bg-white print:p-0">
      <style>{`@media print { .no-print { display: none !important; } @page { margin: 14mm; } }`}</style>

      <div className="max-w-3xl mx-auto mb-4 flex items-center justify-between no-print">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#C85A32]" data-testid="bordereau-back">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg" data-testid="bordereau-print">
          <Printer className="w-4 h-4" /> Imprimer
        </button>
      </div>

      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow print:shadow-none p-8" data-testid="bordereau-sheet">
        <div className="flex items-start justify-between border-b border-slate-200 pb-4 mb-5">
          <div className="flex items-center gap-3">
            {c?.logo ? <img src={c.logo} alt="" className="h-14 w-auto" /> : <div className="w-12 h-12 rounded-lg bg-[#C85A32] flex items-center justify-center text-white font-display font-black text-xl">T</div>}
            <div>
              <p className="font-display text-2xl font-black text-[#0F172A]">{c?.name || "TOGOBATI"}</p>
              {c?.address && <p className="text-xs text-slate-500">{c.address}</p>}
              <p className="text-xs text-slate-500">{[c?.phone, c?.email].filter(Boolean).join(" · ")}</p>
              {(c?.rccm || c?.nif) && <p className="text-[10px] text-slate-400">{[c?.rccm && `RCCM ${c.rccm}`, c?.nif && `NIF ${c.nif}`].filter(Boolean).join(" · ")}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="font-display text-lg font-extrabold uppercase text-[#C85A32]">Bordereau de livraison</p>
            <p className="text-sm font-num font-bold">{d.order_number}</p>
            <p className="text-xs text-slate-500">{d.date ? formatDate(d.date) : formatDate(d.created_at)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-5 text-sm">
          <div>
            <p className="text-xs font-bold uppercase text-slate-400 mb-1">Client</p>
            <p className="font-semibold text-slate-800">{d.customer_name}</p>
            {o?.customer_phone && <p className="text-slate-600">{o.customer_phone}</p>}
            <p className="text-slate-600">{d.address?.address || ""}</p>
            <p className="text-slate-600">{d.zone}{d.address?.quartier ? ` · ${d.address.quartier}` : ""}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase text-slate-400 mb-1">Livraison</p>
            <p className="text-slate-600">Tournée : <span className="font-semibold text-slate-800">{t?.tour_number || "—"}</span></p>
            <p className="text-slate-600">Chauffeur : <span className="font-semibold text-slate-800">{d.driver_name || t?.driver_name || "—"}</span></p>
            <p className="text-slate-600">Véhicule : <span className="font-semibold text-slate-800">{[d.vehicle || t?.vehicle, d.vehicle_plate || t?.vehicle_plate].filter(Boolean).join(" · ") || "—"}</span></p>
          </div>
        </div>

        <table className="w-full text-sm border-collapse mb-5">
          <thead>
            <tr className="bg-slate-50 text-left">
              <th className="border border-slate-200 px-3 py-2 font-semibold">Article</th>
              <th className="border border-slate-200 px-3 py-2 font-semibold text-center w-20">Qté</th>
              <th className="border border-slate-200 px-3 py-2 font-semibold text-right w-28">Prix U.</th>
              <th className="border border-slate-200 px-3 py-2 font-semibold text-right w-28">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i, idx) => (
              <tr key={idx}>
                <td className="border border-slate-200 px-3 py-2">{i.name}</td>
                <td className="border border-slate-200 px-3 py-2 text-center font-num">{i.quantity} {i.unit || ""}</td>
                <td className="border border-slate-200 px-3 py-2 text-right font-num">{formatFCFA(i.price)}</td>
                <td className="border border-slate-200 px-3 py-2 text-right font-num">{formatFCFA(i.price * i.quantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end mb-8">
          <div className="w-64 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Sous-total</span><span className="font-num">{formatFCFA(o?.subtotal || 0)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Livraison</span><span className="font-num">{formatFCFA(o?.delivery_fee || 0)}</span></div>
            <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-1"><span>Total</span><span className="font-num text-[#C85A32]">{formatFCFA(o?.total || 0)}</span></div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 text-xs text-slate-500 pt-6">
          <div className="border-t border-slate-300 pt-2">Signature du livreur</div>
          <div className="border-t border-slate-300 pt-2">Signature du client (réception)</div>
        </div>
      </div>
    </div>
  );
}
